import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../../config/firebase';
import { signOut } from 'firebase/auth';
import { useAuth } from '../../context/AuthContext';
import Icon from './Icons';
import logo from '../../logo.png';
import { AiOutlineMenu, AiOutlineClose } from 'react-icons/ai';

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole, currentUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < 768
  );

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) setIsOpen(false);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  function handleNavigate(path) {
    navigate(path);
    if (isMobile) setIsOpen(false);
  }

  const adminLinks = [
    { path: '/admin', icon: 'dashboard', label: 'Dashboard' },
    { path: '/programs', icon: 'projects', label: 'Programu' },
    { path: '/programs/insights', icon: 'chart', label: 'Chess & Lifeskills' },
    { path: '/rating/admin', icon: 'trophy', label: 'Rating Rasmi (IFR)' },
    { path: '/rating/live', icon: 'sparkle', label: 'Tukio la Papo Hapo' },
    { path: '/announcements', icon: 'bolt', label: 'Matangazo & Ratiba' },
    { path: '/staff/weekly', icon: 'user', label: 'Ripoti za Staff' },
    { path: '/admin/students', icon: 'students', label: 'Kumbukumbu: Wanafunzi' },
    { path: '/admin/parents', icon: 'parents', label: 'Kumbukumbu: Wazazi' },
    { path: '/admin/staff', icon: 'staff', label: 'Staff' },
    { path: '/admin/reports', icon: 'report', label: 'Kumbukumbu: Ripoti' },
    { path: '/admin/chess-accounts', icon: 'chess', label: 'Chess Accounts' },
    { path: '/admin/ai-insights', icon: 'ai', label: 'AI Insights' },
    { path: '/admin/monthly-reports', icon: 'dashboard', label: 'Monthly Reports' },
    { path: '/admin/projects', icon: 'projects', label: 'Projects' },
    { path: '/admin/chess-students', icon: 'chess', label: 'Chess Registry' },
    { path: '/admin/meetings', icon: 'notes', label: 'Meeting Minutes' },
    { path: '/admin/payments', icon: 'card', label: 'Payment Settings' },
    { path: '/admin/program-names', icon: 'notes', label: 'Majina ya Programu' },
    { path: '/admin/duplicate-members', icon: 'link', label: 'Wanachama Wanaorudia' },
    { path: '/admin/maintenance', icon: 'wrench', label: 'Maintenance Mode' },
    { path: '/office-chess', icon: 'trophy', label: 'Mashindano ya Ofisini' },
    { path: '/account', icon: 'user', label: 'Akaunti Yangu' },
    { path: '/chess', icon: 'chess', label: 'Chess App' },
    { path: '/chess/tournaments', icon: 'trophy', label: 'Tournaments' },
  ];

  const staffLinks = [
    { path: '/staff', icon: 'home', label: 'Home' },
    { path: '/programs', icon: 'projects', label: 'Programu' },
    { path: '/programs/insights', icon: 'chart', label: 'Chess & Lifeskills' },
    { path: '/rating/admin', icon: 'trophy', label: 'Rating Rasmi (IFR)' },
    { path: '/rating/live', icon: 'sparkle', label: 'Tukio la Papo Hapo' },
    { path: '/staff/weekly', icon: 'user', label: 'Ripoti Yangu ya Wiki' },
    { path: '/announcements', icon: 'bolt', label: 'Matangazo & Ratiba' },
    { path: '/staff/students', icon: 'students', label: 'Kumbukumbu: Wanafunzi' },
    { path: '/staff/parents', icon: 'parents', label: 'Kumbukumbu: Wazazi' },
    { path: '/staff/meetings', icon: 'notes', label: 'Meeting Minutes' },
    { path: '/office-chess', icon: 'trophy', label: 'Mashindano ya Ofisini' },
    { path: '/account', icon: 'user', label: 'Akaunti Yangu' },
    { path: '/chess', icon: 'chess', label: 'Chess App' },
    { path: '/chess/tournaments', icon: 'trophy', label: 'Tournaments' },
  ];

  const donorLinks = [
    { path: '/donor', icon: 'home', label: 'Overview' },
    { path: '/donor/payment', icon: 'heart', label: 'Donate Online' },
    { path: '/donor/ai-assistant', icon: 'ai', label: 'AI Assistant' },
    { path: '/chess', icon: 'chess', label: 'Chess App' },
  ];

  // Public visitor — not logged in at all, browsing the public DonorHome
  const publicLinks = [
    { path: '/donor', icon: 'home', label: 'Home' },
    { path: '/chess', icon: 'chess', label: 'Chess App' },
    { path: '/chess/tournaments', icon: 'trophy', label: 'Tournaments' },
    { path: '/rating', icon: 'verified', label: 'Rating Rasmi (IFR)' },
  ];

  const links =
    userRole === 'admin' ? adminLinks :
    userRole === 'staff' ? staffLinks :
    userRole === 'donor' ? donorLinks :
    publicLinks;

  async function handleLogout() {
    await signOut(auth);
    navigate('/login', { replace: true });
  }

  const sidebarContent = (
    <div style={styles.sidebarInner}>
      <div style={styles.logoArea}>
        <div style={styles.logoMark}>
          <img
            src={logo}
            alt="Iyanna Foundation"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={styles.logoName}>
            Iyanna Foundation
          </div>
          <div style={styles.logoRole}>
            {userRole === 'admin' ? 'Admin Portal' :
             userRole === 'staff' ? 'Staff Portal' :
             userRole === 'donor' ? 'Donor Portal' :
             'Community'}
          </div>
        </div>
        {isMobile && (
          <button
            style={styles.closeBtn}
            onClick={() => setIsOpen(false)}
          >
            <AiOutlineClose size={20} color="#fff" />
          </button>
        )}
      </div>

      <div style={styles.navArea}>
        {links.map((link) => (
          <div
            key={link.path}
            onClick={() => handleNavigate(link.path)}
            style={{
              ...styles.navItem,
              ...(location.pathname === link.path
                ? styles.navActive : {})
            }}
          >
            <span style={styles.navIcon}><Icon name={link.icon} size={17} /></span>
            {link.label}
          </div>
        ))}
      </div>

      <div style={styles.bottom}>
        {currentUser ? (
          <div onClick={handleLogout} style={styles.logout}>
            <Icon name="logout" size={15} /> Sign Out
          </div>
        ) : (
          <div
            onClick={() => handleNavigate('/login')}
            style={styles.adminPortalLink}
            title="Organization Login"
          >
            <Icon name="key" size={13} /> Admin/Staff Portal
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {isMobile && (
        <div style={styles.mobileHeader}>
          <button
            style={styles.menuBtn}
            onClick={() => setIsOpen(true)}
          >
            <AiOutlineMenu size={22} color="#fff" />
          </button>
          <div style={styles.mobileLogoRow}>
            <div style={styles.mobileLogoMark}>
              <img
                src={logo}
                alt="Iyanna Foundation"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
            <span style={styles.mobileLogoName}>
              Iyanna Foundation
            </span>
          </div>
          <div style={styles.mobileRole}>
            {userRole === 'admin' ? <Icon name="staff" size={18} /> :
             userRole === 'staff' ? <Icon name="user" size={18} /> :
             userRole === 'donor' ? <Icon name="heart" size={18} /> : <Icon name="seedling" size={18} />}
          </div>
        </div>
      )}

      {!isMobile && (
        <div style={styles.sidebar}>
          {sidebarContent}
        </div>
      )}

      {isMobile && isOpen && (
        <>
          <div
            style={styles.overlay}
            onClick={() => setIsOpen(false)}
          />
          <div style={styles.mobileSidebar}>
            {sidebarContent}
          </div>
        </>
      )}
    </>
  );
}

const styles = {
  sidebar: {
    width: '245px',
    flexShrink: 0,
    background: 'linear-gradient(180deg, #041F14 0%, #07271A 50%, #14452F 100%)',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    position: 'sticky',
    top: 0,
    height: '100vh',
    overflowY: 'auto',
  },
  mobileSidebar: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '280px',
    height: '100vh',
    background: 'linear-gradient(180deg, #041F14 0%, #07271A 50%, #14452F 100%)',
    zIndex: 1000,
    overflowY: 'auto',
    boxShadow: '4px 0 24px rgba(0,0,0,0.4)',
  },
  sidebarInner: {
    display: 'flex',
    flexDirection: 'column',
    padding: '22px 0 16px',
    minHeight: '100%',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 999,
    backdropFilter: 'blur(2px)',
  },
  mobileHeader: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: '56px',
    background: 'linear-gradient(90deg, #041F14, #14452F)',
    display: 'flex',
    alignItems: 'center',
    padding: '0 16px',
    zIndex: 100,
    boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
    gap: '12px',
  },
  menuBtn: {
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    borderRadius: '8px',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  mobileLogoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
  },
  mobileLogoMark: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    overflow: 'hidden',
    background: '#041F14',
    border: '1.5px solid rgba(255,255,255,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0px',
    flexShrink: 0,
  },
  mobileLogoName: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '15px',
    color: '#fff',
    fontWeight: '700',
  },
  mobileRole: { fontSize: '20px' },
  closeBtn: {
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    borderRadius: '8px',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  logoArea: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '0 18px 24px',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    marginBottom: '12px',
  },
  logoMark: {
    width: '45px',
    height: '45px',
    borderRadius: '50%',
    overflow: 'hidden',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#041F14',
    padding: '0px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    border: '2px solid rgba(255,255,255,0.15)',
  },
  logoName: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '13px',
    color: '#fff',
    lineHeight: 1.2,
  },
  logoRole: {
    fontSize: '10px',
    color: 'rgba(255,255,255,0.35)',
    marginTop: '2px',
  },
  navArea: {
    flex: 1,
    padding: '0 0 16px',
    overflowY: 'auto',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '11px 18px',
    color: 'rgba(255,255,255,0.55)',
    fontSize: '13px',
    cursor: 'pointer',
    borderLeft: '3px solid transparent',
    transition: 'all 0.18s',
  },
  navActive: {
    background: 'linear-gradient(90deg, rgba(245,124,0,0.15), transparent)',
    color: '#FFA726',
    borderLeft: '3px solid #F57C00',
  },
  navIcon: {
    fontSize: '15px',
    width: '20px',
    textAlign: 'center',
  },
  bottom: {
    padding: '16px 18px 0',
    borderTop: '1px solid rgba(255,255,255,0.07)',
  },
  logout: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '10px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  adminPortalLink: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: '11px',
    cursor: 'pointer',
    padding: '10px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    letterSpacing: '0.2px',
  },
};